import { useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";

type Report = {
  import_id?: string;
  parsed_rows?: number;
  voters_processed?: number;
  unique_addresses_in_file?: number;
  addresses_created_or_upserted?: number;
  address_changes_detected?: number;
  missing_voter_id_rows?: number;
  notes?: string[];
  source?: string;
  error?: string;
  details?: any;
};

export default function ImportVoters() {
  const campaignId = useCampaignId();

  const [importType, setImportType] = useState<"cameron" | "generic">("cameron");
  const [county, setCounty] = useState<string>("");
  const [stateOverride, setStateOverride] = useState<string>("TX");
  const [profileId, setProfileId] = useState<string>("");

  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);

  const canImport = useMemo(() => !!(campaignId && file), [campaignId, file]);

  async function doImport() {
    setErr(null);
    setMsg(null);
    setReport(null);

    if (!campaignId || !file) {
      setErr("Choose a campaign and a file.");
      return;
    }

    try {
      setBusy(true);

      const { data: session } = await supabase.auth.getSession();
      const accessToken = session.session?.access_token;
      if (!accessToken) {
        setErr("You must be signed in.");
        setBusy(false);
        return;
      }

      const form = new FormData();
      form.append("file", file);
      form.append("campaign_id", campaignId);

      let fnUrl = "";

      if (importType === "cameron") {
        if (county.trim()) form.append("county", county.trim());
        if (stateOverride.trim()) form.append("state", stateOverride.trim());
        fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/import_voters_cameron`;
      } else {
        fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/import_voters_generic`;
        if (profileId.trim()) form.append("profile_id", profileId.trim());
      }

      const res = await fetch(fnUrl, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form,
      });

      const text = await res.text();
      let json: Report;
      try { json = JSON.parse(text); } catch { json = { error: "Failed to parse response", details: text as any }; }

      if (!res.ok || json.error) {
        setErr(json.error || `Import failed with status ${res.status}`);
        setReport(json);
      } else {
        setMsg("Import complete.");
        setReport(json);
      }
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
        Import Registered Voters
      </h1>

      {err && (
        <div
          style={{
            background: "#ffe8e8",
            border: "1px solid #f2b0b0",
            padding: 8,
            borderRadius: 6,
            margin: "10px 0",
            color: "#a40000",
            whiteSpace: "pre-wrap",
          }}
        >
          {err}
        </div>
      )}
      {msg && (
        <div
          style={{
            background: "#ecfdf5",
            border: "1px solid #86efac",
            padding: 8,
            borderRadius: 6,
            margin: "10px 0",
            color: "#166534",
          }}
        >
          {msg}
        </div>
      )}

      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ display: "grid", gap: 10 }}>
          {/* campaign selector removed — uses top bar */}

          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ width: 160 }}>Import Type</span>
            <select
              value={importType}
              onChange={(e) =>
                setImportType(e.target.value as "cameron" | "generic")
              }
              style={{
                padding: 8,
                border: "1px solid #ddd",
                borderRadius: 8,
                minWidth: 280,
              }}
            >
              <option value="cameron">Cameron County (CSV/XLSX)</option>
              <option value="generic">Generic / Profile (CSV/XLSX)</option>
            </select>
          </label>

          {importType === "cameron" && (
            <>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ width: 160 }}>County (optional)</span>
                <input
                  placeholder="e.g., Cameron"
                  value={county}
                  onChange={(e) => setCounty(e.target.value)}
                  style={{
                    padding: 8,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    minWidth: 280,
                  }}
                />
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ width: 160 }}>State (optional)</span>
                <input
                  placeholder="TX"
                  value={stateOverride}
                  onChange={(e) => setStateOverride(e.target.value)}
                  style={{
                    padding: 8,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    minWidth: 120,
                  }}
                />
              </label>
            </>
          )}

          {importType === "generic" && (
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ width: 160 }}>Profile ID (optional)</span>
              <input
                placeholder="import_profiles.id"
                value={profileId}
                onChange={(e) => setProfileId(e.target.value)}
                style={{
                  padding: 8,
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  minWidth: 280,
                }}
              />
            </label>
          )}

          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ width: 160 }}>File</span>
            <input
              type="file"
              accept=".csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              style={{ padding: 6 }}
            />
          </label>

          <div style={{ color: "#6b7280", fontSize: 13 }}>
            This page uploads your file to the appropriate Edge Function:
            <ul style={{ marginLeft: 16 }}>
              <li>
                <b>Cameron County</b> → <code>import_voters_cameron</code>
              </li>
              <li>
                <b>Generic/Profile</b> → <code>import_voters_generic</code>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={doImport}
          disabled={!canImport || busy}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #0ea5e9",
            background: canImport && !busy ? "#0ea5e9" : "#9ccff0",
            color: "#fff",
            cursor: canImport && !busy ? "pointer" : "not-allowed",
          }}
        >
          {busy ? "Importing…" : "Import"}
        </button>
      </div>

      {report && (
        <div
          style={{
            marginTop: 16,
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 12,
            whiteSpace: "pre-wrap",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Import Report</div>
          <div>{JSON.stringify(report, null, 2)}</div>
        </div>
      )}
    </div>
  );
}

